// =====================================================================
// ReconciliationTab (Financeiro Conciliação / S63) — importa OFX do banco e
// casa com o extrato do sistema. Auto-match no import; match/ignorar manual.
// Componente auto-contido (recebe accounts + onChanged) — mantém o index enxuto.
// =====================================================================
import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box, Button, Chip, CircularProgress, IconButton, MenuItem, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography
} from "@material-ui/core";
import LinkIcon from "@material-ui/icons/Link";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import BlockIcon from "@material-ui/icons/Block";
import PublishIcon from "@material-ui/icons/Publish";
import { toast } from "react-toastify";
import {
  listStatementLines, importStatement, unmatchStatementLine, ignoreStatementLine,
  fmtMoney, financeErrorMessage
} from "../../services/financeService";
import ReconcileMatchDialog from "./ReconcileMatchDialog";

const useStyles = makeStyles((theme) => ({
  toolbar: { display: "flex", gap: theme.spacing(2), alignItems: "center", flexWrap: "wrap", margin: theme.spacing(1, 0, 2) },
  empty: { padding: theme.spacing(6), textAlign: "center", color: theme.palette.text.secondary },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: 160 }
}));

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

const STATUS = {
  pendente: { label: "Pendente", color: "#b26a00" },
  conciliado: { label: "Conciliado", color: "#2e7d32" },
  ignorado: { label: "Ignorado", color: "#757575" }
};

const ReconciliationTab = ({ accounts = [], onChanged }) => {
  const classes = useStyles();
  const active = accounts.filter((a) => a.active);
  const [accountId, setAccountId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [data, setData] = useState({ lines: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [matchLine, setMatchLine] = useState(null);

  useEffect(() => {
    if (!accountId && active.length) {
      setAccountId(String((active.find((a) => a.isDefault) || active[0]).id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const fetchLines = useCallback(async () => {
    if (!accountId) { setData({ lines: [], summary: {} }); return; }
    setLoading(true);
    try {
      setData(await listStatementLines({ accountId, status: statusFilter || undefined }));
    } catch (e) {
      toast.error("Falha ao carregar a conciliação.");
      setData({ lines: [], summary: {} });
    } finally { setLoading(false); }
  }, [accountId, statusFilter]);

  useEffect(() => { fetchLines(); }, [fetchLines]);

  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    if (!accountId) { toast.error("Selecione a conta primeiro."); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setImporting(true);
      try {
        const r = await importStatement(accountId, String(reader.result || ""));
        toast.success(
          `OFX importado: ${r.imported} linha(s) · ${r.matched} casada(s) · ${r.pending} pendente(s)` +
          (r.skipped ? ` · ${r.skipped} já existia(m)` : "") + "."
        );
        fetchLines();
        onChanged && onChanged();
      } catch (err) {
        toast.error(financeErrorMessage(err));
      } finally { setImporting(false); }
    };
    reader.readAsText(f);
  };

  const handleUnmatch = async (line) => {
    try { await unmatchStatementLine(line.id); fetchLines(); onChanged && onChanged(); }
    catch (err) { toast.error(financeErrorMessage(err)); }
  };
  const handleIgnore = async (line) => {
    try { await ignoreStatementLine(line.id); fetchLines(); onChanged && onChanged(); }
    catch (err) { toast.error(financeErrorMessage(err)); }
  };

  const s = data.summary || {};

  return (
    <>
      <div className={classes.toolbar}>
        <TextField select size="small" variant="outlined" label="Conta" value={accountId}
          onChange={(e) => setAccountId(e.target.value)} style={{ flexGrow: 1, minWidth: 130 }}>
          {active.map((a) => <MenuItem key={a.id} value={String(a.id)}>{a.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" variant="outlined" label="Filtro" value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)} style={{ flexGrow: 1, minWidth: 130 }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="pendente">Pendentes</MenuItem>
          <MenuItem value="conciliado">Conciliadas</MenuItem>
          <MenuItem value="ignorado">Ignoradas</MenuItem>
        </TextField>
        <Button variant="contained" color="primary" component="label" size="small"
          startIcon={importing ? <CircularProgress size={16} /> : <PublishIcon />} disabled={importing || !accountId}>
          Importar OFX
          <input type="file" accept=".ofx,text/plain,application/x-ofx" hidden onChange={handleFile} />
        </Button>
        <Box flexGrow={1} />
        <Chip size="small" label={`${s.conciliado || 0} conciliadas`} style={{ backgroundColor: "#2e7d32", color: "#fff" }} />
        <Chip size="small" label={`${s.pendente || 0} pendentes`} style={{ backgroundColor: "#b26a00", color: "#fff" }} />
        {!!s.ignorado && <Chip size="small" label={`${s.ignorado} ignoradas`} />}
      </div>

      {loading ? (
        <div className={classes.loading}><CircularProgress /></div>
      ) : (data.lines || []).length === 0 ? (
        <div className={classes.empty}>
          <Typography>Sem linhas importadas. Clique em "Importar OFX" e selecione o extrato do banco.</Typography>
        </div>
      ) : (
        <div style={{ overflowX: "auto", width: "100%" }}>
        <Table size="small" style={{ minWidth: 640 }}>
          <TableHead><TableRow>
            <TableCell>Data</TableCell><TableCell>Descrição (banco)</TableCell>
            <TableCell align="right">Valor</TableCell><TableCell align="center">Status</TableCell>
            <TableCell>Casado com</TableCell><TableCell align="center" style={{ width: 120 }}>Ações</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {data.lines.map((l) => {
              const st = STATUS[l.status] || { label: l.status, color: "#757575" };
              const positive = Number(l.amount) >= 0;
              return (
                <TableRow key={l.id} hover>
                  <TableCell>{fmtDate(l.postedAt)}</TableCell>
                  <TableCell>{l.memo || "—"}</TableCell>
                  <TableCell align="right" style={{ color: positive ? "#2e7d32" : "#c62828", fontWeight: 600 }}>
                    {fmtMoney(l.amount)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={st.label} style={{ backgroundColor: st.color, color: "#fff" }} />
                  </TableCell>
                  <TableCell>
                    {l.matchedEntryId
                      ? <Typography variant="caption">{l.matchedDescription || "lançamento"} · {fmtDate(l.matchedDate)}</Typography>
                      : "—"}
                  </TableCell>
                  <TableCell align="center">
                    {l.status === "conciliado" ? (
                      <Tooltip title="Desfazer conciliação"><IconButton size="small" onClick={() => handleUnmatch(l)}><LinkOffIcon fontSize="small" /></IconButton></Tooltip>
                    ) : l.status === "pendente" ? (
                      <>
                        <Tooltip title="Casar com lançamento"><IconButton size="small" color="primary" onClick={() => setMatchLine(l)}><LinkIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Ignorar linha"><IconButton size="small" onClick={() => handleIgnore(l)}><BlockIcon fontSize="small" /></IconButton></Tooltip>
                      </>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        </div>
      )}

      <ReconcileMatchDialog open={!!matchLine} line={matchLine}
        onClose={() => setMatchLine(null)}
        onMatched={() => { setMatchLine(null); fetchLines(); onChanged && onChanged(); }} />
    </>
  );
};

export default ReconciliationTab;
